import { ChevronDown, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface BranchOption {
	id: string
	name: string
}

interface BranchSelectorProps {
	branches: BranchOption[]
	selected: string[]
	onChange: (selected: string[]) => void
	/** Override the section heading. Falls back to the questionnaires i18n key. */
	title?: string
}

export function BranchSelector({
	branches,
	selected,
	onChange,
	title,
}: BranchSelectorProps) {
	const { t } = useTranslation()
	const selectAllRef = useRef<HTMLInputElement>(null)
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')

	const filtered = search.trim()
		? branches.filter((b) =>
				b.name.toLowerCase().includes(search.trim().toLowerCase()),
			)
		: branches

	const allSelected = selected.length === branches.length
	const noneSelected = selected.length === 0

	useEffect(() => {
		if (selectAllRef.current) {
			selectAllRef.current.indeterminate = !noneSelected && !allSelected
		}
	}, [allSelected, noneSelected])

	const handleToggleAll = () => {
		onChange(allSelected ? [] : branches.map((b) => b.id))
	}

	const handleToggle = (id: string) => {
		onChange(
			selected.includes(id)
				? selected.filter((s) => s !== id)
				: [...selected, id],
		)
	}

	return (
		<div className="rounded-lg border border-kaefer-light-silver/60 overflow-hidden">
			{/* Header / toggle */}
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="flex w-full cursor-pointer items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
			>
				<div className="flex items-center gap-2">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{title ?? t('administration.questionnairesForm.branchesTitle')}
					</span>
					{selected.length > 0 && (
						<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-kaefer-medium-grey/20 px-1.5 text-[11px] font-semibold tabular-nums text-kaefer-medium-grey">
							{selected.length}
						</span>
					)}
				</div>
				<ChevronDown
					className={cn(
						'size-4 text-kaefer-medium-grey transition-transform duration-200',
						open && 'rotate-180',
					)}
				/>
			</button>

			{/* Body */}
			{open && (
				<div className="flex flex-col gap-3 p-3 border-t border-kaefer-light-silver/40">
					{/* Search + select all row */}
					<div className="flex items-center gap-2">
						<div className="relative flex-1">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-kaefer-medium-grey/60 pointer-events-none" />
							<Input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder={t(
									'administration.questionnairesForm.branchSearchPlaceholder',
								)}
								className="h-8 pl-8 text-sm"
							/>
						</div>
						<label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-md border border-kaefer-light-silver/60 bg-background px-3 py-1.5 text-sm font-medium text-kaefer-dark-grey hover:bg-muted/40 transition-colors">
							<input
								ref={selectAllRef}
								type="checkbox"
								checked={allSelected}
								onChange={handleToggleAll}
								className="size-4 cursor-pointer rounded border-kaefer-light-silver accent-kaefer-medium-grey"
							/>
							{t('administration.questionnairesForm.selectAll')}
						</label>
					</div>

					{/* Branch grid */}
					{filtered.length === 0 ? (
						<p className="py-4 text-center text-sm text-kaefer-medium-grey/70">
							{t('administration.questionnairesForm.noBranchesFound')}
						</p>
					) : (
						<div className="max-h-60 overflow-y-auto pr-1">
							<div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
								{filtered.map((branch) => {
									const isChecked = selected.includes(branch.id)
									return (
										<label
											key={branch.id}
											title={branch.name}
											className={cn(
												'flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm transition-colors',
												isChecked
													? 'border-kaefer-medium-grey/40 bg-kaefer-medium-grey/8 text-kaefer-dark-grey'
													: 'border-transparent hover:border-kaefer-light-silver/60 hover:bg-muted/40 text-kaefer-dark-grey/80',
											)}
										>
											<input
												type="checkbox"
												checked={isChecked}
												onChange={() => handleToggle(branch.id)}
												className="size-4 shrink-0 cursor-pointer rounded border-kaefer-light-silver accent-kaefer-medium-grey"
											/>
											<span className="truncate text-sm leading-snug">
												{branch.name}
											</span>
										</label>
									)
								})}
							</div>
						</div>
					)}

					{/* Footer count */}
					<p className="text-right text-[11px] text-kaefer-medium-grey/70">
						{selected.length} / {branches.length}{' '}
						{t('administration.questionnairesForm.branchesSelected')}
					</p>
				</div>
			)}
		</div>
	)
}
